import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  cancelBuildDev,
  getBuildLogs,
  getBuildRunLog,
  runBuildDev,
} from "@/lib/build-logs.functions";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const PHASES: Array<{ key: string; label: string; match: RegExp }> = [
  { key: "spawn", label: "Starting", match: /=== build:dev started/ },
  { key: "vite", label: "Vite booting", match: /\$ vite build|vite v\d/ },
  { key: "client-transform", label: "Transforming client modules", match: /transforming \(/ },
  { key: "client-render", label: "Rendering client chunks", match: /rendering chunks/ },
  { key: "client-gzip", label: "Computing gzip sizes", match: /computing gzip/ },
  { key: "client-done", label: "Client bundle written", match: /dist\/client\// },
  { key: "ssr", label: "Building SSR bundle", match: /dist\/server\/_ssr|dist\/server\/_libs/ },
  { key: "ssr-done", label: "Server bundle written", match: /dist\/server\/index\.mjs/ },
  { key: "nitro", label: "Generating Nitro worker", match: /\[nitro\]/ },
  { key: "wrangler", label: "Generating wrangler config", match: /wrangler\.json/ },
  { key: "done", label: "Deploy artifacts ready", match: /You can deploy this build|build:dev exited code=0/ },
];

function computeProgress(content: string, exitCode: number | null) {
  const reached = PHASES.map((p) => p.match.test(content));
  let lastIdx = -1;
  reached.forEach((hit, i) => {
    if (hit) lastIdx = i;
  });
  const failed = exitCode != null && exitCode !== 0;
  const completed = exitCode === 0 || PHASES[PHASES.length - 1].match.test(content);
  if (completed) lastIdx = PHASES.length - 1;
  const currentIdx = lastIdx < 0 ? 0 : lastIdx;
  const percent = Math.round(((currentIdx + 1) / PHASES.length) * 100);
  return { reached, currentIdx, percent, failed, completed };
}

export const Route = createFileRoute("/_authenticated/admin/build-logs")({
  component: BuildLogsPage,
});

function BuildLogsPage() {
  const [autoRefresh, setAutoRefresh] = useState(false);
  const fetchLogs = useServerFn(getBuildLogs);
  const fetchRunLog = useServerFn(getBuildRunLog);
  const startBuild = useServerFn(runBuildDev);
  const cancelBuild = useServerFn(cancelBuildDev);
  const { data, isFetching, refetch, error } = useQuery({
    queryKey: ["admin", "build-logs"],
    queryFn: () => fetchLogs(),
    refetchInterval: autoRefresh ? 5_000 : false,
    refetchOnWindowFocus: false,
  });

  const {
    data: runLog,
    refetch: refetchRunLog,
  } = useQuery({
    queryKey: ["admin", "build-run-log"],
    queryFn: () => fetchRunLog(),
    refetchInterval: (q) => {
      // Stream every 2s while a run is active; otherwise follow auto-refresh.
      if (q.state.data?.running) return 2_000;
      return autoRefresh ? 5_000 : false;
    },
    refetchOnWindowFocus: false,
  });

  const runMutation = useMutation({
    mutationFn: () => startBuild(),
    onSuccess: () => {
      // Kick the stream immediately and enable auto-refresh.
      setAutoRefresh(true);
      refetchRunLog();
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelBuild(),
    onSuccess: () => refetchRunLog(),
  });

  // While a run is active, ensure the dev-server log also refreshes.
  useEffect(() => {
    if (runLog?.running) {
      const id = setInterval(() => refetch(), 2_000);
      return () => clearInterval(id);
    }
  }, [runLog?.running, refetch]);

  return (
    <div className="space-y-4">
      <header className="flex flex-col items-start justify-between gap-3 sm:flex-row">
        <div>
          <h2 className="text-lg font-semibold">Build & dev-server logs</h2>
          <p className="text-xs text-muted-foreground">
            Tail of the sandbox Vite/Rolldown dev-server log, with error/warn lines highlighted.
            Only the last ~200 KB are shown.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 rounded border border-white/10 bg-card/40 px-2 py-1 text-xs text-muted-foreground">
            <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh} />
            <span>Auto-refresh (5s)</span>
            {autoRefresh && (
              <span className="flex items-center gap-1 text-foreground">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
                Live
              </span>
            )}
          </label>
          <Button
            size="sm"
            variant="secondary"
            disabled={runMutation.isPending || runLog?.running}
            onClick={() => runMutation.mutate()}
          >
            {runLog?.running ? "Building…" : runMutation.isPending ? "Starting…" : "Run build:dev"}
          </Button>
          <Button
            size="sm"
            variant="destructive"
            disabled={!runLog?.running || cancelMutation.isPending}
            onClick={() => cancelMutation.mutate()}
          >
            {cancelMutation.isPending ? "Stopping…" : "Cancel"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!data?.content}
            onClick={() => {
              if (!data?.content) return;
              const blob = new Blob([data.content], { type: "text/plain" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `build-logs-${new Date(data.fetchedAt).toISOString()}.txt`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }}
          >
            Download .txt
          </Button>
          <Button size="sm" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </header>

      {error && (
        <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300">
          {(error as Error).message}
        </div>
      )}

      {runMutation.data?.started === false && (
        <div className="rounded border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200">
          Could not start build:dev: {runMutation.data.error}
        </div>
      )}

      {cancelMutation.data?.canceled === false && cancelMutation.data?.error && (
        <div className="rounded border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200">
          Could not cancel: {cancelMutation.data.error}
        </div>
      )}

      {(runLog?.available || runLog?.running) && (
        <section className="rounded border border-primary/30 bg-primary/5 p-3">
          {(() => {
            const progress = computeProgress(runLog?.content ?? "", runLog?.exitCode ?? null);
            const accent = progress.failed
              ? "bg-red-500"
              : progress.completed
              ? "bg-emerald-500"
              : "bg-primary";
            return (
              <div className="mb-3 space-y-2">
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {progress.failed
                      ? `Failed at: ${PHASES[progress.currentIdx].label}`
                      : progress.completed
                      ? "Build complete"
                      : `Step ${progress.currentIdx + 1}/${PHASES.length}: ${PHASES[progress.currentIdx].label}`}
                  </span>
                  <span>{progress.percent}%</span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/5">
                  <div
                    className={`h-full ${accent} transition-[width] duration-500`}
                    style={{ width: `${progress.percent}%` }}
                  />
                </div>
                <ol className="flex flex-wrap gap-1.5 text-[10px]">
                  {PHASES.map((p, i) => {
                    const hit = progress.reached[i] || i <= progress.currentIdx;
                    const isCurrent = i === progress.currentIdx && !progress.completed && !progress.failed && runLog?.running;
                    return (
                      <li
                        key={p.key}
                        className={`rounded border px-1.5 py-0.5 ${
                          progress.failed && i === progress.currentIdx
                            ? "border-red-500/50 bg-red-500/10 text-red-200"
                            : hit
                            ? "border-primary/40 bg-primary/10 text-foreground"
                            : "border-white/10 bg-transparent text-muted-foreground/60"
                        }`}
                      >
                        {isCurrent && (
                          <span className="mr-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary align-middle" />
                        )}
                        {p.label}
                      </li>
                    );
                  })}
                </ol>
              </div>
            );
          })()}
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-primary">
              build:dev output
              {runLog?.running && (
                <span className="ml-2 inline-flex items-center gap-1 text-foreground">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
                  streaming
                </span>
              )}
              {runLog?.exitCode != null && (
                <span
                  className={`ml-2 ${runLog.exitCode === 0 ? "text-emerald-300" : "text-red-300"}`}
                >
                  exit {runLog.exitCode}
                </span>
              )}
            </h3>
            <div className="text-[10px] text-muted-foreground">
              {runLog?.startedAt && <>started {new Date(runLog.startedAt).toLocaleTimeString()} · </>}
              {runLog?.bytes.toLocaleString()} B
            </div>
          </div>
          <pre className="max-h-[400px] overflow-auto whitespace-pre-wrap break-all text-[11px] leading-relaxed text-foreground/90">
            {runLog?.content || "(waiting for output…)"}
          </pre>
        </section>
      )}

      {data && (
        <>
          <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-4">
            <Meta label="Available" value={data.available ? "yes" : "no"} />
            <Meta label="Size" value={`${data.bytes.toLocaleString()} B`} />
            <Meta label="Truncated" value={data.truncated ? "yes" : "no"} />
            <Meta label="Fetched" value={new Date(data.fetchedAt).toLocaleTimeString()} />
          </div>

          {data.note && (
            <div className="rounded border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
              {data.note}
            </div>
          )}

          {data.errors.length > 0 && (
            <section className="rounded border border-red-500/30 bg-red-500/5 p-3">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-red-300">
                Error / warn lines ({data.errors.length})
              </h3>
              <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all text-[11px] leading-relaxed text-red-200">
                {data.errors.join("\n")}
              </pre>
            </section>
          )}

          <section className="rounded border border-white/10 bg-background/60 p-3">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Full tail
            </h3>
            <pre className="max-h-[600px] overflow-auto whitespace-pre-wrap break-all text-[11px] leading-relaxed text-muted-foreground">
              {data.content || "(empty)"}
            </pre>
          </section>
        </>
      )}
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-white/10 bg-card/40 px-2 py-1">
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="font-mono text-foreground">{value}</div>
    </div>
  );
}